/**
 * iter648 — iPad-friendly print toast.
 *
 * Polls /api/cashier/print-bridge/history every 5 s for jobs in the last
 * 10 minutes belonging to the cashier's branch and toasts:
 *   • done     → success toast ("Receipt printed to <printer>")
 *   • failed   → error toast with a Re-print action
 *   • claimed  → no toast (intermediate)
 *
 * Designed to run once near the root of the POS so the cashier sees
 * confirmation without having to navigate to /print-history. Native iOS
 * Web-Push requires the page to be installed as a PWA (iOS 16.4+) — a
 * future enhancement. This polling approach works everywhere today.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

const POLL_INTERVAL_MS = 12_000; // status toasts only — printing itself is not delayed
const WINDOW_HOURS = 1;

export function PrintJobToaster({ enabled = true }) {
  const seenRef = useRef(new Map()); // id → last status

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const poll = async () => {
      if (document.hidden || cancelled) return;
      try {
        const { data } = await api.get(`/cashier/print-bridge/history?hours=${WINDOW_HOURS}&limit=30`);
        const jobs = data.jobs || [];
        for (const j of jobs) {
          const prev = seenRef.current.get(j.id);
          if (prev === j.status) continue;
          seenRef.current.set(j.id, j.status);
          // Skip toast on FIRST sighting of stable states — we only want
          // to notify on actual transitions during this session.
          if (prev === undefined) continue;
          const printerLabel = j.printer_ip || 'printer';
          if (j.status === 'done') {
            toast.success(`🖨️ Printed to ${printerLabel}`, { id: `pj-${j.id}` });
          } else if (j.status === 'failed') {
            toast.error(`Print failed → ${printerLabel} · ${j.error || 'check Bridge'}`, {
              id: `pj-${j.id}`,
              duration: 8_000,
              action: {
                label: 'Re-print',
                onClick: async () => {
                  try {
                    await api.post(`/cashier/print-bridge/jobs/${j.id}/retry`);
                    toast.success('Re-queued');
                  } catch {
                    toast.error('Retry failed');
                  }
                },
              },
            });
          }
        }
        // Trim seen map (avoid unbounded growth).
        if (seenRef.current.size > 200) {
          const half = Array.from(seenRef.current.entries()).slice(-100);
          seenRef.current = new Map(half);
        }
      } catch {
        // Silent — endpoint may not exist on older deploys.
      }
    };

    const id = setInterval(poll, POLL_INTERVAL_MS);
    const onVis = () => { if (!document.hidden) poll(); };
    const onKick = () => { setTimeout(poll, 2500); setTimeout(poll, 6000); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pos:print-sent', onKick);
    poll();
    return () => { cancelled = true; clearInterval(id); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('pos:print-sent', onKick); };
  }, [enabled]);

  return null; // headless
}
